import React, { useState, useEffect, useRef } from "react";
import { FaEdit } from "react-icons/fa";
import AddPatientModal from "../AllComponents/AddPatientModal";
import "./Campaign.css";
import "rsuite/dist/rsuite.min.css";
import { toast } from "react-toastify";
import { getCampaignReferralPatients, searchCampaignReferralPatients, updateSalesStatus } from "../../api/admin/dashboard";
import CustomDropdown from "../resuableComponents/CustomDropdown";
import PaginationComponent from "../AllComponents/Shared/PaginationComponent";

const CONTACTED_STATUS_OPTIONS = [
  { value: "maybe", label: "May be" },
  { value: "not_converted", label: "Not interested" },
  { value: "interested", label: "Interested" },
];

const contactedStatusLabels = Object.fromEntries(
  CONTACTED_STATUS_OPTIONS.map((option) => [option.value, option.label])
);

const formatContactedStatus = (value) => {
  if (!value) return "—";
  if (contactedStatusLabels[value]) return contactedStatusLabels[value];
  return String(value).replace(/_/g, " ");
};

const COLUMN_COUNT = 7;

const SalesFollowUp = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 20, 30, 50, 100];
  const searchTimeoutRef = useRef(null);

  // Status Modal State
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [contactedStatus, setContactedStatus] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [limit]);

  useEffect(() => {
    fetchPatients(currentPage);
  }, [currentPage, limit]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const fetchPatients = async (page = 1) => {
    setLoading(true);
    try {
      if (searchTerm.trim()) {
        const response = await searchCampaignReferralPatients("", searchTerm.trim(), page, limit);
        if (response.data && response.data.success) {
          setPatients(response.data.data.patients || []);
          setTotalPatients(response.data.data.count || 0);
          setTotalPages(response.data.data.totalPages || 1);
        } else {
          setPatients([]);
        }
      } else {
        const response = await getCampaignReferralPatients(page, limit);
        if (response.data && response.data.success) {
          setPatients(response.data.data.patients || []);
          setTotalPatients(response.data.data.count || 0);
          setTotalPages(response.data.data.totalPages || 1);
        } else {
          setPatients([]);
        }
      }
    } catch (err) {
      console.error("Error fetching camp records:", err);
      toast.error("Failed to fetch camp records.");
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page
      fetchPatients(1);
    }, 800);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const handlePatientAdded = (newPatient) => {
    setCurrentPage(1);
    fetchPatients(1);
  };

  const openStatusModal = (patient) => {
    setSelectedPatient(patient);
    setNewStatus(patient.sales_status || "pending");
    setContactedStatus(patient.contacted_status || "");
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setSelectedPatient(null);
    setNewStatus("");
    setContactedStatus("");
  };

  const handleMainStatusChange = (value) => {
    setNewStatus(value);
    if (String(value).toLowerCase() !== "contacted") {
      setContactedStatus("");
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedPatient || !newStatus) return;
    const isContacted = String(newStatus).toLowerCase() === "contacted";
    if (isContacted && !contactedStatus) {
      toast.error("Select a contacted status.");
      return;
    }
    
    setStatusUpdating(true);
    try {
      const response = await updateSalesStatus(
        selectedPatient._id,
        newStatus,
        isContacted ? contactedStatus : undefined
      );
      if (response.data && response.data.success) {
        toast.success("Status updated successfully");
        // Refetch the entire page to reflect changes accurately
        fetchPatients(currentPage);
        closeStatusModal();
      } else {
        throw new Error(response.data?.message || "Failed to update");
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to update status");
    } finally {
      setStatusUpdating(false);
    }
  };

  return (
    <div className="camp-surface">
      <div className="camp-shell">
        <header className="camp-hero" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="camp-hero-copy">
            <p className="camp-chip">Sales Operations</p>
            <h1>
              Camp Registrations
              <span> at a glance.</span>
            </h1>
            <p style={{ marginTop: '0.5rem', color: '#64748b' }}>
              Track potential patients acquired from camps, manage their status, and focus on converting them to hospital visits.
            </p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            style={{
              padding: "10px 20px", borderRadius: "8px", border: "none",
              background: "#198754", color: "white", fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap"
            }}
          >
            <span style={{ fontSize: "1.2rem" }}>+</span>
            New patient
          </button>
        </header>

        <section className="camp-search-block">
          <div className="camp-search-field">
            <svg
              className="camp-search-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by name or contact…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <span className="camp-count">
            {searchTerm
              ? `Showing ${totalPatients} results${loading ? "…" : ""}`
              : `All camp registrations (${totalPatients})`}
          </span>
        </section>

        <section className="camp-table-wrapper">
          <table className="camp-table">
            <thead>
              <tr>
                <th>Patient Name</th>
                <th>Mobile</th>
                <th>Camp Source</th>
                <th>Registered</th>
                <th>Status</th>
                <th>Customer Response</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: Math.min(limit, 8) }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="camp-skeleton-row">
                    {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
                      <td key={cell}>
                        <div className="camp-skeleton-cell" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : patients.length > 0 ? (
                patients.map((patient, idx) => {
                  const statusLower = (patient.sales_status || "pending").toLowerCase();
                  const isContacted = statusLower === "contacted";
                  const statusClass =
                    statusLower === "converted"
                      ? "converted"
                      : isContacted
                      ? "contacted"
                      : "pending";

                  return (
                    <tr key={patient._id || idx}>
                      <td className="camp-name">{patient.name || "N/A"}</td>
                      <td className="camp-nowrap">{patient.contact || "—"}</td>
                      <td className="camp-campaign-cell">
                        <span className="camp-badge camp-campaign" title={patient.campaign_name || ""}>
                          {patient.campaign_name || "—"}
                        </span>
                      </td>
                      <td className="camp-nowrap">
                        {patient.createdAt
                          ? new Date(patient.createdAt).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td>
                        <span className={`camp-badge camp-status ${statusClass}`}>
                          {patient.sales_status || "Pending"}
                        </span>
                      </td>
                      <td>
                        {isContacted ? (
                          <span
                            className={`camp-badge camp-response ${patient.contacted_status || "unknown"}`}
                          >
                            {formatContactedStatus(patient.contacted_status)}
                          </span>
                        ) : (
                          <span className="camp-muted">—</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="camp-icon-btn"
                          title="Update status"
                          onClick={() => openStatusModal(patient)}
                        >
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="camp-empty">
                    <div className="camp-empty-inner">
                      <strong>No camp registrations found.</strong>
                      <p>Camp registrations will appear here.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
        
        {!loading && totalPatients > limit && (
          <PaginationComponent
            total={totalPatients}
            limit={limit}
            limitOptions={limitOptions}
            activePage={currentPage}
            onChangePage={setCurrentPage}
            onChangeLimit={(newLimit) => setLimit(newLimit)}
            className="camp-pagination"
            style={{ marginTop: "20px" }}
          />
        )}
      </div>

      <AddPatientModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onPatientAdded={handlePatientAdded}
        fetchPatients={() => fetchPatients(currentPage)}
      />

      {/* Status Update Modal */}
      {statusModalOpen && selectedPatient && (
        <div className="modal-overlay" style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
          padding: "10px"
        }}>
          <div className="status-modal-content" style={{
            background: "white", padding: "2rem", borderRadius: "16px",
            width: "90%", maxWidth: "420px", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)"
          }}>
            <h3 style={{ margin: "0 0 0.5rem 0", color: "#0f172a", fontSize: "1.25rem" }}>Update Status</h3>
            <p style={{ margin: "0 0 1.5rem 0", color: "#64748b", fontSize: "0.95rem" }}>
              Update sales pipeline status for <strong style={{ color: "#334155" }}>{selectedPatient.name}</strong>
            </p>
            
            <div style={{ marginBottom: "2rem" }}>
              <CustomDropdown
                label="Select Status"
                value={newStatus}
                onChange={handleMainStatusChange}
                options={[
                  { value: "pending", label: "Pending" },
                  { value: "contacted", label: "Contacted" },
                  { value: "converted", label: "Converted" }
                ]}
              />
              {String(newStatus).toLowerCase() === "contacted" && (
                <div style={{ marginTop: "16px" }}>
                  <CustomDropdown
                    label="Contacted Status"
                    value={contactedStatus}
                    onChange={setContactedStatus}
                    options={CONTACTED_STATUS_OPTIONS}
                  />
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                className="status-modal-cancel"
                onClick={closeStatusModal}
                style={{
                  padding: "10px 16px", borderRadius: "8px", border: "1px solid #cbd5e1",
                  background: "transparent", color: "#475569", fontWeight: 600, cursor: "pointer"
                }}
                disabled={statusUpdating}
              >
                Cancel
              </button>
              <button 
                onClick={handleStatusUpdate}
                style={{
                  padding: "10px 20px", borderRadius: "8px", border: "none",
                  background: "#198754", color: "white", fontWeight: 600, cursor: "pointer",
                  opacity: statusUpdating ? 0.7 : 1
                }}
                disabled={statusUpdating}
              >
                {statusUpdating ? "Updating..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesFollowUp;
